import styled, { css } from "styled-components";

import {
  inlineElementHeightStyles,
  inlineElementTypography,
  controlRadius,
  controlTransition,
} from "../../foundations/controlStyles";
import type { InputSize } from "./inputTypes";

// 세로 패딩은 높이를 만드는 값이 아니라 최소 여백일 뿐 — 높이는 inlineElementHeightStyles의 min-height가
// 잡고 내용은 align-items:center로 수직 중앙 정렬한다. 단일 줄 높이(폰트 line-height + 세로 패딩 +
// 테두리)가 min-height를 넘으면 컨트롤이 계약보다 커지므로(기존 36px 회귀) 패딩을 넉넉히 아래로 둔다.
const inputSizeStyles: Record<InputSize, ReturnType<typeof css>> = {
  sm: css`
    padding: 0.1875rem 0.6rem;
    font-size: ${inlineElementTypography.sm.fontSize};
    line-height: ${inlineElementTypography.sm.lineHeight};
    ${controlRadius}
  `,
  md: css`
    padding: 0.1875rem 0.6rem;
    font-size: ${inlineElementTypography.md.fontSize};
    line-height: ${inlineElementTypography.md.lineHeight};
    ${controlRadius}
  `,
};

type InputControlProps = {
  $inputSize?: InputSize;
  $invalid?: boolean;
  $disabled?: boolean;
  $readOnly?: boolean;
  $fullWidth?: boolean;
};

export const InputFieldRow = styled.div<{ $fullWidth?: boolean }>`
  width: ${({ $fullWidth = true }) => ($fullWidth ? "100%" : "auto")};
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const InputControl = styled.div<InputControlProps>`
  width: ${({ $fullWidth = true }) => ($fullWidth ? "100%" : "auto")};
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${({ theme }) => theme.color.text.default};
  background-color: transparent;
  /* 입력류 rest 보더는 subtle — ContentSwitcher 트랙과 같은 단으로 톤다운(가장자리가
     형제 컨트롤보다 튄다는 리뷰). 3:1 대비는 hover(strong)/focus에서 확보. */
  border: 1px solid ${({ theme }) => theme.color.border.subtle};
  ${controlTransition("border-color", "background-color")}

  ${({ $inputSize = "md" }) => inlineElementHeightStyles($inputSize)}
  ${({ $inputSize = "md" }) => inputSizeStyles[$inputSize]}

  &:hover {
    border-color: ${({ theme }) => theme.color.border.strong};
  }

  &:has(:focus-visible) {
    border-color: ${({ theme }) => theme.color.border.focus};
  }

  ${({ $invalid, theme }) =>
    $invalid &&
    css`
      border-color: ${theme.color.border.danger};
      &:hover,
      &:has(:focus-visible) {
        border-color: ${theme.color.border.danger};
      }
      &:has(:focus-visible) {
        outline: 1px solid ${theme.color.border.focus};
        outline-offset: 1px;
      }
    `}

  ${({ $readOnly, theme }) =>
    $readOnly &&
    css`
      background-color: ${theme.color.bg.surface};
    `}

  ${({ $disabled, theme }) =>
    $disabled &&
    css`
      background-color: ${theme.color.bg.surfaceSunken};
      border-color: ${theme.color.border.disabled};
      color: ${theme.color.text.disabled};
      cursor: not-allowed;
      pointer-events: none;
      user-select: none;
    `}
`;

export const StyledBaseInput = styled.input`
  width: 100%;
  flex: 1 1 auto;
  min-width: 0;
  outline: none;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  /* InputControl의 size별 공용 font-size/line-height를 함께 상속한다. 결정적 토큰 값이라
     placeholder/값 script에 따른 UA line-height:normal 드리프트 없이 min-height가 바인딩된다. */

  &::placeholder {
    color: ${({ theme }) => theme.color.text.subtle};
  }

  &:disabled {
    color: ${({ theme }) => theme.color.text.disabled};
    -webkit-text-fill-color: ${({ theme }) => theme.color.text.disabled};
    cursor: not-allowed;
  }

  &:disabled::placeholder {
    color: ${({ theme }) => theme.color.text.disabled};
  }

  &[type="search"]::-webkit-search-decoration,
  &[type="search"]::-webkit-search-cancel-button,
  &[type="search"]::-webkit-search-results-button,
  &[type="search"]::-webkit-search-results-decoration {
    appearance: none;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    appearance: none;
    margin: 0;
  }
`;

export const InputAdornment = styled.div`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.color.text.subtle};
`;

export const InputTrailingAction = styled.div`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;
